// # Command Line: Added real-time inventory count aggregation and server-side limit handling.
import axios from "axios";
import moment from "moment";
import { message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getAuthTokens,
  getTenantId,
} from "../../../redux/features/auth/authSlice";
import { apiRoutes, baseURL } from "../../../config/api";
import { useGetInventoryDetailsQuery } from "../../../redux/services/inventoryApiSlice";
import { removeEmptyKeys } from "../../../utils";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { Page } from "../../../routes/config";
import { store } from "../../../redux/store";

import { ReportToolbar } from "../../common/reportToolbar";
import { UIAgGridColumn, UIAgGridTable } from "../../common/agGridTable";
import { Pagination } from "../../common/pagination";
import Select from "../../common/selectInput/Select";
import Input from "../../common/input/Input";
import { ArrowRight, Layers, MapPin, X } from "lucide-react";
import EpcisHistoryModal from "./epcisHistoryModal";
import { useGetTrackNTraceTagEpcisQuery } from "../../../redux/services/trackntraceApiSlice";
import { InventoryHeaderSkeleton } from "./components/InventoryHeaderSkeleton";

type InventoryDetailsRow = {
  key: string;
  skuPc: string;
  tagId: string;
  /** Resolved identifier for track/trace (EPCIS) API — diId, identity, or tag display id */
  diLookup: string;
  epc: string;
  siteName: string;
  zoneName: string;
  description: string;
  dateTime: string;
  productName: string;
};

const filterRows = (rows: any[], skuOrUpc?: string, tagIdFilter?: string) => {
  if (!Array.isArray(rows)) return [];
  const needle = String(skuOrUpc ?? "")
    .trim()
    .toLowerCase();
  const tagIdNeedle = String(tagIdFilter ?? "")
    .trim()
    .toLowerCase();
  if (!needle && !tagIdNeedle) return rows;

  return rows.filter((r: any) => {
    const value = String(r?.sku ?? r?.upc ?? "")
      .trim()
      .toLowerCase();
    const tagIdValue = String(
      r?.tagId ?? r?.primaryId ?? r?.diId ?? r?.identityId ?? r?.epc ?? "",
    )
      .trim()
      .toLowerCase();
    const skuMatch = !needle || value === needle;
    const tagIdMatch = !tagIdNeedle || tagIdValue.includes(tagIdNeedle);
    return skuMatch && tagIdMatch;
  });
};

const mapRow = (r: any, index: number): InventoryDetailsRow => {
  const epc = String(r.epc ?? "");
  const tagId = String(
    r.tagId ?? r.primaryId ?? r.diId ?? r.identityId ?? r.epc ?? "",
  );
  const diLookup =
    String(r.diId ?? r.identityId ?? r.primaryId ?? "").trim() || tagId;
  const description = String(
    r.description ??
      r.productDescription ??
      r.product?.description ??
      r.product?.name ??
      r.itemDescription ??
      r.materialDescription ??
      "",
  ).trim();
  return {
    key: String(r.epc ?? r.tagId ?? r.primaryId ?? r.id ?? `row-${index}`),
    skuPc: String(r.sku ?? r.upc ?? ""),
    tagId,
    diLookup,
    epc,
    siteName: String(r.siteName ?? r.site?.name ?? ""),
    zoneName: String(r.zoneName ?? r.zone?.name ?? ""),
    description,
    dateTime: String(
      r.operationTime ?? r.lastUpdatedAt ?? r.updatedAt ?? r.createdAt ?? "",
    ),
    productName: r.productName,
  };
};

const InventoryDetails = ({
  tagEpcisRoute = Page.REPORTS_INVENTORY_TAG_EPCIS,
  requireSku = true,
}: {
  tagEpcisRoute?: string;
  requireSku?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const skuParam = searchParams.get("sku") ?? undefined;
  const siteIdQuery = searchParams.get("siteId");
  const zoneIdQuery = searchParams.get("zoneId");
  const receivedProcessSession =
    searchParams.get("receivedProcessSession") ?? "";

  const tenantId = useSelector(getTenantId);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [siteId, setSiteId] = useState<string | undefined>(
    () => siteIdQuery ?? undefined,
  );
  const [zoneId, setZoneId] = useState<string | undefined>(
    () => zoneIdQuery ?? undefined,
  );
  const [diIdForApi, setDiIdForApi] = useState("");
  const [isEpcisModalVisible, setIsEpcisModalVisible] = useState(false);
  const [epcisRecord, setEpcisRecord] = useState<any>(null);
  const [tagId, setTagId] = useState("");
  const [appliedSiteId, setAppliedSiteId] = useState<string | undefined>(
    () => siteIdQuery ?? undefined,
  );
  const [appliedZoneId, setAppliedZoneId] = useState<string | undefined>(
    () => zoneIdQuery ?? undefined,
  );

  const [epcisPagination, setEpcisPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    setSiteId(siteIdQuery ?? undefined);
    setZoneId(zoneIdQuery ?? undefined);
    setAppliedSiteId(siteIdQuery ?? undefined);
    setAppliedZoneId(zoneIdQuery ?? undefined);
  }, [siteIdQuery, zoneIdQuery]);
  const [appliedTagId, setAppliedTagId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { zoneRows, siteNameById, siteOptionsById, zoneOptionsById } =
    useTenantSitesAndZones(tenantId);

  const queryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        siteId: appliedSiteId || undefined,
        zoneId: appliedZoneId || undefined,
        upc: skuParam || undefined,
        lastProcessSession: receivedProcessSession || undefined,
        page,
        limit: pageSize,
      }),
    [
      tenantId,
      appliedSiteId,
      appliedZoneId,
      skuParam,
      receivedProcessSession,
      page,
      pageSize,
    ],
  );

  const { data, isFetching } = useGetInventoryDetailsQuery(queryParams, {
    skip: !tenantId,
  });

  const epcisQuery = useGetTrackNTraceTagEpcisQuery(
    {
      tenant: tenantId,
      productUPC: skuParam,
      siteId: appliedSiteId || undefined,
      zoneId: appliedZoneId || undefined,
      diId: diIdForApi,
      page,
      pageSize: 1000,
    },
    {
      skip: !tenantId || !diIdForApi,
    },
  );

  const result = useMemo(() => data?.result ?? data, [data]);
  const rows = useMemo(() => result?.rows ?? result?.data ?? [], [result]);
  const filteredRows = useMemo(
    () => filterRows(rows, skuParam, appliedTagId),
    [rows, skuParam, appliedTagId],
  );
  const totalCount = filteredRows.length;

  const tableData: InventoryDetailsRow[] = useMemo(
    () => filteredRows.map((r: any, i: number) => mapRow(r, i)),
    [filteredRows],
  );

  const getExportParams = useCallback(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        siteId: appliedSiteId || undefined,
        zoneId: appliedZoneId || undefined,
        sku: skuParam || undefined,
        page: 1,
        limit: 10000,
      }),
    [tenantId, appliedSiteId, appliedZoneId, skuParam],
  );

  const fetchDataForExport = useCallback((params: Record<string, unknown>) => {
    const st = store.getState();
    const tokens = getAuthTokens(st) as {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };
    const tid = getTenantId(st);
    return axios.get(`${baseURL}${apiRoutes.INVENTORY_DETAILS}`, {
      params,
      headers: {
        "id-token": tokens?.idToken ?? "",
        "access-token": tokens?.accessToken ?? "",
        "refresh-token": tokens?.refreshToken ?? "",
        ...(tid ? { "tenant-id": tid } : {}),
      },
    });
  }, []);

  const exportCsv = useCallback(() => {
    fetchDataForExport(getExportParams())
      .then((res) => {
        const result = res?.data?.result ?? res?.data;
        const rows = result?.rows ?? [];
        const arr = filterRows(rows, skuParam, appliedTagId).map(
          (r: any, i: number) => mapRow(r, i),
        );
        const header = ["SKU/UPC", "Tag ID", "Description", "Site", "Zone"];
        const csvContent = [
          header.join(","),
          ...arr.map((r: InventoryDetailsRow) =>
            [r.skuPc, r.tagId, r.description, r.siteName, r.zoneName]
              .map((c) => `"${String(c).replace(/"/g, '""')}"`)
              .join(","),
          ),
        ].join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `inventory-details-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        message.success("CSV exported.");
      })
      .catch(() => message.error("Export failed."));
  }, [getExportParams, fetchDataForExport, skuParam, appliedTagId]);

  const exportExcel = useCallback(() => {
    fetchDataForExport(getExportParams())
      .then((res) => {
        const result = res?.data?.result ?? res?.data;
        const rows = result?.rows ?? [];
        const arr = filterRows(rows, skuParam, appliedTagId).map(
          (r: any, i: number) => mapRow(r, i),
        );
        const header = ["SKU/UPC", "Tag ID", "Description", "Site", "Zone"];
        const csvContent = [
          header.join("\t"),
          ...arr.map((r: InventoryDetailsRow) =>
            [r.skuPc, r.tagId, r.description, r.siteName, r.zoneName].join(
              "\t",
            ),
          ),
        ].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], {
          type: "application/vnd.ms-excel;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `inventory-details-${new Date().toISOString().slice(0, 10)}.xls`;
        a.click();
        URL.revokeObjectURL(url);
        message.success("Excel exported.");
      })
      .catch(() => message.error("Export failed."));
  }, [getExportParams, fetchDataForExport, skuParam, appliedTagId]);

  const ALL_COLUMNS = useMemo<UIAgGridColumn<InventoryDetailsRow>[]>(() => {
    return [
      {
        key: "epc",
        title: "EPC",
        dataIndex: "epc",
        minWidth: 140,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "tagId",
        title: "Tag ID",
        dataIndex: "tagId",
        minWidth: 140,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },

      {
        key: "dateTime",
        title: "Time",
        dataIndex: "dateTime",
        minWidth: 150,
        flex: 1,
        sorter: true,
        render: (value: unknown) =>
          value ? moment(String(value)).format("DD MMM YY hh:mm A") : "-",
      },
    ];
  }, []);
  const applyFilters = useCallback(() => {
    setAppliedSiteId(siteId || undefined);
    setAppliedZoneId(zoneId || undefined);
    setAppliedTagId(tagId.trim());
    setPage(1);
  }, [siteId, zoneId, tagId]);

  const resetFilters = useCallback(() => {
    setSiteId(undefined);
    setZoneId(undefined);
    setTagId("");
    setAppliedSiteId(undefined);
    setAppliedZoneId(undefined);
    setAppliedTagId("");
    setPage(1);
  }, []);

  const hasFilters = requireSku
    ? !!skuParam
    : !!(skuParam || appliedSiteId || siteIdQuery || receivedProcessSession);

  if (!hasFilters) {
    return (
      <div className="di-reports-page">
        <p className="details-missing-user">
          No filters selected. Click a row on the Live Inventory report to view
          details.
        </p>
      </div>
    );
  }

  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter((col) => !hiddenColumns.has(col.key)),
    [ALL_COLUMNS, hiddenColumns],
  );

  const [columns, setColumns] = useState(visibleColumns);

  useEffect(() => {
    setColumns(visibleColumns);
  }, [visibleColumns]);

  return (
    <div className="scTheme h-full flex flex-col min-h-0  rounded-md gap-3">
      {isFetching ? (
        <InventoryHeaderSkeleton />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 items-stretch">
          {/* Left Section: Product Details Banner */}
          <div className="xl:col-span-3 bg-gradient-to-r from-white via-slate-50/40 to-white border border-slate-200/80 rounded-2xl shadow-xs p-5 flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
            {/* Micro decorative top border accent line */}
            <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-[var(--primary)] via-[rgb(var(--primaryRgb)/0.6)] to-[var(--actionOrange)]" />

            <div className="flex items-center gap-5 min-w-0 flex-1">
              {/* Visual Product Box Placeholder */}
              <div className="w-16 h-16 bg-[var(--primaryLight)] border border-[rgb(var(--primaryRgb)/0.15)] rounded-xl flex items-center justify-center flex-shrink-0 shadow-inner group relative">
                {/* Updated background opacity to use the primary variable */}
                <div className="absolute inset-0 bg-[color:var(--primary)]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />

                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  // Updated text color to use the primary variable
                  className="text-[color:var(--primary)] drop-shadow-xs"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>

              {/* Identity Information and Meta Stringing */}
              <div className="space-y-1.5 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 bg-[var(--primaryLight)] text-[var(--primary)] border border-[rgb(var(--primaryRgb)/0.25)] rounded-full text-[11px] font-bold uppercase tracking-wider font-mono">
                    UPC: {tableData && tableData[0]?.skuPc}
                  </span>
                </div>

                <h1
                  className="text-xl font-extrabold text-[var(--headingTextColor)] tracking-tight truncate max-w-lg"
                  id="hoisted-product-name"
                >
                  {tableData && tableData[0]?.productName}
                </h1>

                {tableData && tableData[0]?.description && (
                  <p className="text-xs text-slate-500 font-medium line-clamp-1 max-w-xl leading-relaxed">
                    {tableData[0]?.description}
                  </p>
                )}
              </div>
            </div>

            {/* Right Section: Location Route Tags */}
            <div className="flex items-center justify-between md:justify-end gap-6 md:pl-6 md:border-l border-slate-200/80 shrink-0">
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block font-sans">
                  Filtered Location Domain
                </span>
                <div className="flex items-center gap-2 text-slate-700 font-semibold text-xs">
                  <span className="inline-flex items-center gap-1.5 bg-[var(--primaryLight)] text-[var(--primary)] border border-[rgb(var(--primaryRgb)/0.2)] px-2.5 py-1 rounded-lg">
                    <MapPin className="w-3.5 h-3.5 text-[var(--primary)]" />{" "}
                    {tableData && tableData[0]?.siteName}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                  <span className="inline-flex items-center gap-1.5 text-[#B8710A] bg-[#FFF1DC] border border-[rgb(var(--actionOrangeRgb)/0.3)] px-2.5 py-1 rounded-lg">
                    <Layers className="w-3.5 h-3.5 text-[var(--actionOrange)]" />{" "}
                    {tableData && tableData[0]?.zoneName}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Inline Total Scanned Metric Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs flex items-center justify-between transition-all hover:border-slate-300 relative overflow-hidden group">
            {/* Micro background gradient glow on hover */}
            <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full blur-xl bg-[rgb(var(--actionOrangeRgb)/0.1)] group-hover:bg-[rgb(var(--actionOrangeRgb)/0.2)] transition-all duration-300" />

            <div className="space-y-1 z-10">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block font-sans">
                Total Scanned Items
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-[var(--headingTextColor)] tracking-tight font-mono">
                  {tableData ? tableData.length : 0}
                </span>
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  Units
                </span>
              </div>
            </div>

            {/* Elegant Stack Layer Graphic Icon Panel */}
            <div className="p-3 bg-[#FFF4E5] rounded-xl border border-[rgb(var(--actionOrangeRgb)/0.25)] text-[var(--actionOrange)] transition-transform group-hover:scale-105 duration-300 shrink-0 shadow-xs">
              <Layers className="w-6 h-6 drop-shadow-xs" />
            </div>
          </div>
        </div>
      )}

      <div className="h-full flex flex-col min-h-0 gap-0">
        <ReportToolbar
          searchValue={tagId}
          onSearchChange={(value) => setTagId(value)}
          searchPlaceholder="Search Tag ID"
          onSearchEnter={applyFilters}
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() =>
            setShowAdvancedFilters((show) => !show)
          }
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          onExportExcel={exportExcel}
          hideAdvancedFilters
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Site */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Site
              </label>
              <Select
                placeholder="Select Site"
                value={siteId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSiteId(value);
                  setZoneId("");
                }}
                onClear={() => {
                  setSiteId("");
                  setZoneId("");
                }}
                allowClear
                options={siteOptionsById}
              />
            </div>

            {/* Zone */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Zone
              </label>
              <Select
                placeholder="Select Zone"
                value={zoneId}
                onChange={(e) => {
                  const value = e.target.value;
                  setZoneId(value);

                  const z = zoneRows.find(
                    (r) => String(r.id) === String(value),
                  );
                  if (z?.siteId) setSiteId(String(z.siteId));
                }}
                onClear={() => setZoneId("")}
                allowClear
                options={zoneOptionsById}
              />
            </div>

            {/* Tag ID */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Tag ID
              </label>
              <Input
                placeholder="Please enter Tag ID"
                value={tagId}
                onChange={(e) => setTagId(e.target.value)}
                allowClear
              />
            </div>
          </div>
        </ReportToolbar>

        <div className="table-container">
          <UIAgGridTable
            columns={columns}
            data={tableData}
            loading={isFetching}
            rowHeight={44}
            onRowClick={(record: InventoryDetailsRow) => {
              setDiIdForApi(record.diLookup);
              setEpcisRecord({
                productDescription: record.description || record.productName,
                productUPC: record.skuPc,
                siteName: record.siteName,
                zoneName: record.zoneName,
              });
              setIsEpcisModalVisible(true);
            }}
          />
          <Pagination
            current={page}
            pageSize={pageSize}
            className="rounded-b-md border-t border-slate-200 dark:border-gray-700"
            total={totalCount}
            hideOnSinglePage={false}
            showSizeChanger={true}
            onPageChange={(p) => {
              setPage(p);
            }}
            onPageSizeChange={(newSize) => {
              setPage(1);
              setPageSize(newSize);
            }}
          />
        </div>
      </div>

      <EpcisHistoryModal
        visible={isEpcisModalVisible}
        onClose={() => setIsEpcisModalVisible(false)}
        data={epcisQuery?.data}
        loading={epcisQuery?.isFetching}
        diId={diIdForApi}
        record={epcisRecord}
      />
    </div>
  );
};

export default InventoryDetails;
